import styles from './ComplementaryOffer.module.scss';
import { Button } from 'src/v2/commonComponents/Button';
import { useCancelOffers, useGetUser, useSubscriptionInfo } from 'src/hooks';
import { useOfferedMonths } from 'src/hooks/paymentHooks/useOfferedMonths';
import dayjs from 'dayjs';
import { CancelSubscriptionSteps } from 'src/v2/pages/subscription/components/CancelSubscription';
import { ComplementaryOfferPlanItem } from 'src/v2/pages/subscription/components/CancelSubscription/components/ComplementaryOfferPlanItem';
import { AppRoutes, UserSettingsNestedRoutes } from 'src/types';
import { useClaimOfferMutation } from 'src/store/services';
import { toast } from 'react-toastify';
import { COMMON_ERROR_TEXT } from 'src/constants';
import { useNavigate } from 'react-router-dom';

type ComplementaryOfferProps = {
  onChangeStep: (value: number) => void;
};

export const ComplementaryOffer = ({
  onChangeStep,
}: ComplementaryOfferProps) => {
  const { user_id } = useGetUser();
  const navigate = useNavigate();
  const { cancelOffers } = useCancelOffers();
  const { months: offeredMonths } = useOfferedMonths({
    context: 'accept',
    offerType: cancelOffers?.complimentary_offer?.offer_type,
  });
  const offeredOneMonth = offeredMonths === 1;
  const [claimOffer, { isLoading }] = useClaimOfferMutation();
  const nextBillingTimeRaw =
    cancelOffers?.complimentary_offer?.next_billing_time;
  const nextBillingDate = nextBillingTimeRaw ? dayjs(nextBillingTimeRaw) : null;
  const { subscriptionInfo } = useSubscriptionInfo();
  const planToOffer =
    cancelOffers?.complimentary_offer?.user_plan_kind_override_on_offer_claim ||
    subscriptionInfo?.plan_kind;

  const handleGoToSelectReason = async () => {
    try {
      const defaultOffer = cancelOffers?.default_offer;
      if (!!defaultOffer && defaultOffer?.eligible) {
        await claimOffer({
          user_id,
          offer_type: defaultOffer?.offer_type || '',
        }).unwrap();
      }
    } catch (e) {
    } finally {
      onChangeStep(CancelSubscriptionSteps.SELECT_REASON);
    }
  };

  const handleClaimOffer = async () => {
    try {
      await claimOffer({
        user_id,
        offer_type: cancelOffers?.complimentary_offer?.offer_type || '',
      }).unwrap();
      toast.success(
        'Complimentary offer successfully applied to your subscription',
      );
      navigate(
        `${AppRoutes.MANAGE_ACCOUNT}/${UserSettingsNestedRoutes.SUBSCRIPTION}`,
      );
    } catch (error) {
      toast.error(COMMON_ERROR_TEXT);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            A complimentary {offeredMonths}-month offer, just for you
          </h2>
          <p className={styles.subTitle}>
            As a token of appreciation, enjoy {offeredMonths} free{' '}
            {offeredOneMonth ? 'month' : 'months'}
            {nextBillingDate && nextBillingDate.isValid() && (
              <> until {nextBillingDate.format('MMMM D')}</>
            )}
            . <br />
            We will NOT charge you during {offeredOneMonth
              ? 'this'
              : 'these'}{' '}
            {offeredMonths} {offeredOneMonth ? 'month' : 'months'}.
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.planWrapper}>
            <ComplementaryOfferPlanItem planToOffer={planToOffer} />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button onClick={handleClaimOffer} disabled={isLoading}>
          Claim Free {offeredMonths}-month Offer
        </Button>

        <Button
          color="secondary-outline"
          onClick={handleGoToSelectReason}
          disabled={isLoading}
        >
          Reject Free {offeredMonths}-month Offer & Cancel
        </Button>
      </div>
    </div>
  );
};
